import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import SubContent from '../../../generic-components/Layout/SubContent';
import MainContent from '../../../generic-components/Layout/MainContent';
import YearFilter from '../../../generic-components/YearFilter';
import DocsArea from '../../../generic-components/DocsArea';
import ContentWrapper from '../../../generic-components/ContentWrapper';

import { useExpensesListQuery } from '../../../hooks/query/useExpensesListQuery';
import { useStore } from '../../../store';
import { IExpenseObject } from '../../../types';
import { getExistExpensesYears } from '../../../helpers/getYearsArrayFromCreationTime.helper';
import { useTransactionsCategoriesQuery } from '../../../hooks/query/useTransactionsCategoriesQuery';
import StatementExpensesSkeleton from '../../../generic-components/StatementExpensesSkeleton';
import { EXPENSE_REPORTS_PAGE_HEADER, FEATURE_FLAG } from '../../../constants/general';
import { canUpgradeToUseFeature, validateFeatureFlag } from '../../../services/featureFlagService';
import { UpgradeIntro } from '../components/UpgradeIntro';
import { Layout } from '../../../components/Layout/Layout';
import Heading from '../../../generic-components/Heading';

const Expenses = () => {
  const { customerId } = useStore((state) => state.currUser());
  const setCategories = useStore((state) => state.setTransactionCategories);
  const { data: expensesList, isLoading } = useExpensesListQuery({ customerId });
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const currentYearData = expensesList?.taxStatement.filter(
    (exp: IExpenseObject) => Number(exp.endDate.substring(0, 4)) === currentYear,
  );
  const [yearsArr, setYearsArr] = useState<number[]>([]);
  const { data: categoriesList, refetch: getCategoriesList } = useTransactionsCategoriesQuery({ customerId });
  const [shouldShowUpgrade] = useState<boolean>(
    canUpgradeToUseFeature(FEATURE_FLAG.EXPENSE_REPORTS) && !validateFeatureFlag(FEATURE_FLAG.EXPENSE_REPORTS),
  );
  const currentYearUncategorizedDates = expensesList?.taxStatement
    .map((exp: IExpenseObject) => {
      if (Number(exp.endDate.substring(0, 4)) === currentYear) {
        return `endTime=${exp.endDate.substring(0, 10)}&startTime=${exp.startDate.substring(0, 10)}`;
      }
      return '';
    })
    .filter((e: string) => e !== '');
  useEffect(() => {
    (async () => {
      await getCategoriesList();
    })();
  }, []);

  useEffect(() => {
    if (categoriesList) {
      setCategories(categoriesList.categories);
    }
  }, [categoriesList]);

  useEffect(() => {
    setYearsArr(getExistExpensesYears(expensesList?.taxStatement || []));
  }, [expensesList]);

  useEffect(() => {
    if (yearsArr[0]) setCurrentYear(yearsArr[0]);
  }, [yearsArr]);

  const { t } = useTranslation();

  return shouldShowUpgrade ? (
    <UpgradeIntro />
  ) : (
    <Layout wrapperClassName='pt-[78px] pl-[72px] w-screen pr-[32px] ml-[100px] pc-md:ml-[256px]'>
      <Heading
        title={t(EXPENSE_REPORTS_PAGE_HEADER)}
        className='font-metricRSemibold text-[20px] text-bw08 mb-[24px]'
      />
      <ContentWrapper>
        {yearsArr && !!yearsArr.length && (
          <SubContent className='flex flex-col items-center bg-bw00 rounded-xl w-[264px] px-[40px] py-[1.875rem] h-[546px] pc-lg:h-[90%] pc-lg:min-h-[750px]'>
            {isLoading ? (
              <div className='w-full h-full'>
                <Skeleton className='my-4' count={9} />
              </div>
            ) : (
              <YearFilter yearsToDisplay={yearsArr} currentYear={currentYear} setCurrentYear={setCurrentYear} />
            )}
          </SubContent>
        )}
        <MainContent className='flex flex-col bg-bw00 rounded-xl w-[840px] p-[40px] h-[546px] pc-lg:h-[90%] pc-lg:min-h-[750px]'>
          {isLoading && <StatementExpensesSkeleton />}
          {expensesList && currentYearUncategorizedDates && !isLoading && (
            <DocsArea
              year={currentYear}
              docs={currentYearData}
              uncategorizedQuarters={currentYearUncategorizedDates}
              expenses
            />
          )}
        </MainContent>
      </ContentWrapper>
    </Layout>
  );
};

export default Expenses;
